import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ban,
  ChevronRight,
  Loader2,
  Megaphone,
  Pause,
  Play,
  Plus,
  Square,
  Trash2,
} from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_VARIANTS = {
  draft: "outline",
  scheduled: "secondary",
  running: "default",
  paused: "secondary",
  completed: "outline",
  cancelled: "destructive",
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("__all__");
  const [filterAgent, setFilterAgent] = useState("__all__");
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "__all__") params.status = filterStatus;
      if (filterAgent !== "__all__") params.agent_id = filterAgent;
      if (search) params.search = search;
      const [c, a] = await Promise.all([
        api.get("/campaigns", { params }),
        api.get("/agents"),
      ]);
      setCampaigns(c.data);
      setAgents(a.data);
    } catch (err) {
      toastError(err, "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterAgent, search]);

  const lifecycle = async (camp, action) => {
    try {
      await api.post(`/campaigns/${camp.id}/${action}`);
      toast.success(`${action} → ${camp.name}`);
      fetchAll();
    } catch (err) {
      toastError(err, `Failed to ${action}`);
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/campaigns/${confirmDelete.id}`);
      toast.success("Campaign deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toastError(err, "Delete failed");
    }
  };

  const agentName = (id) => agents.find((a) => a.id === id)?.name || "—";

  return (
    <div className="space-y-8" data-testid="campaigns-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Campaigns</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Outbound dial-out: pair a contact list with an agent, schedule it,
            and let the worker dial through. Press Launch to enqueue jobs.
          </p>
        </div>
        <Button onClick={() => navigate("/campaigns/new")} data-testid="campaigns-new-btn">
          <Plus className="h-4 w-4" /> New campaign
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44" data-testid="campaigns-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Agent</Label>
          <Select value={filterAgent} onValueChange={setFilterAgent}>
            <SelectTrigger className="w-52" data-testid="campaigns-filter-agent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All agents</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Search name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="campaigns-filter-search"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="campaigns-empty">
            <div className="rounded-full bg-secondary p-3"><Megaphone className="h-6 w-6 text-muted-foreground" /></div>
            <div className="text-base font-medium">No campaigns yet</div>
            <p className="max-w-md text-sm text-muted-foreground">
              Build a campaign to dial through a contact list using one of your agents.
            </p>
            <Button onClick={() => navigate("/campaigns/new")} className="mt-2">
              <Plus className="h-4 w-4" /> New campaign
            </Button>
          </div>
        ) : (
          <Table data-testid="campaigns-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Concurrency</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => {
                const total = c.stats?.total || 0;
                const done = (c.stats?.completed || 0) + (c.stats?.failed || 0);
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <TableRow key={c.id} data-testid={`campaigns-row-${c.id}`}>
                    <TableCell>
                      <Link
                        to={`/campaigns/${c.id}/control`}
                        className="font-medium hover:underline"
                        data-testid={`campaigns-link-${c.id}`}
                      >
                        {c.name}
                      </Link>
                      {c.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">{c.description}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[c.status] || "outline"}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{agentName(c.agent_id)}</TableCell>
                    <TableCell className="min-w-[160px]">
                      <div className="flex items-center gap-2 text-xs">
                        <div className="h-1.5 flex-1 rounded-full bg-secondary">
                          <div
                            className="h-1.5 rounded-full bg-primary transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="font-mono text-muted-foreground tabular-nums">
                          {done}/{total}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{c.concurrency_cap}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {c.status === "draft" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => lifecycle(c, "launch")}
                          data-testid={`campaigns-launch-${c.id}`}
                        >
                          <Play className="h-4 w-4" /> Launch
                        </Button>
                      )}
                      {c.status === "running" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => lifecycle(c, "pause")}
                          data-testid={`campaigns-pause-${c.id}`}
                        >
                          <Pause className="h-4 w-4" /> Pause
                        </Button>
                      )}
                      {c.status === "paused" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => lifecycle(c, "resume")}
                          data-testid={`campaigns-resume-${c.id}`}
                        >
                          <Play className="h-4 w-4" /> Resume
                        </Button>
                      )}
                      {!["completed", "cancelled"].includes(c.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => lifecycle(c, "cancel")}
                          data-testid={`campaigns-cancel-${c.id}`}
                        >
                          <Square className="h-4 w-4" /> Cancel
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/campaigns/${c.id}`)}
                        data-testid={`campaigns-edit-${c.id}`}
                      >
                        <ChevronRight className="h-4 w-4" /> Open
                      </Button>
                      {["draft", "completed", "cancelled"].includes(c.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDelete(c)}
                          data-testid={`campaigns-delete-${c.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="campaigns-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <b>{confirmDelete?.name}</b> and all of its jobs and events.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="campaigns-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Ban className="h-4 w-4" /> Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
